import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { fmt, fmtDate } from '@dcc-erp/shared';
import type { PettyCashClose } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HttpError } from '../../lib/api';
import {
  useApprovePettyCashCloseStep,
  useRejectPettyCashCloseStep,
  useReturnPettyCashClose,
  useSubmitPettyCashClose,
} from '../../lib/queries/petty-cash';
import { ApprovalTimeline } from '../approval/ApprovalTimeline';
import { DecisionModal, type Decision } from '../approval/DecisionModal';
import { Can } from '../Can';

// ============================================================
// Float-close approval panel (PCX) — clone of PCEntryApprovalPanel. Maker
// (pettycash.close.create) submits a DRAFT/RETURNED close; checker
// (pettycash.close.approve) approves/returns/rejects via DecisionModal. The
// return JV posts + the float flips CLOSED on final-step approval. 2-step chain
// (FINANCE_MANAGER → CFO).
// ============================================================

export interface PCCloseApprovalPanelProps {
  companyId: string | undefined;
  close: PettyCashClose;
  onClose: () => void;
}

export function PCCloseApprovalPanel({ companyId, close, onClose }: PCCloseApprovalPanelProps) {
  const submitMut = useSubmitPettyCashClose(companyId);
  const approveMut = useApprovePettyCashCloseStep(companyId);
  const rejectMut = useRejectPettyCashCloseStep(companyId);
  const returnMut = useReturnPettyCashClose(companyId);

  const [showDecide, setShowDecide] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const qc = useQueryClient();

  const decideBusy = approveMut.isPending || rejectMut.isPending || returnMut.isPending;
  const submitBusy = submitMut.isPending;

  const approvalStatus =
    close.approvalStatus ?? (close.status === 'PENDING_APPROVAL' ? 'PENDING' : null);
  const currentStep = close.currentStep ?? 0;
  const approvals = useMemo(
    () => (close.approvals ?? []).map((a) => ({ ...a, id: a.stepNo })),
    [close.approvals],
  );
  const totalSteps = approvals.length;
  const isPending = approvalStatus === 'PENDING';
  const isResubmittable = approvalStatus === 'DRAFT' || approvalStatus === 'RETURNED';

  async function handleSubmit() {
    setActionError(null);
    try {
      await submitMut.mutateAsync(close.id);
      onClose();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function handleDecide(d: Decision) {
    setActionError(null);
    try {
      if (d.action === 'approve') {
        await approveMut.mutateAsync({
          closeId: close.id,
          step: d.step,
          comment: d.comment,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else if (d.action === 'return') {
        await returnMut.mutateAsync({
          closeId: close.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else {
        await rejectMut.mutateAsync({
          closeId: close.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      }
      setShowDecide(false);
      onClose();
    } catch (e) {
      if (e instanceof HttpError && e.status === 409) {
        setActionError('รายการนี้เปลี่ยนสถานะแล้ว — รีเฟรชข้อมูลให้แล้ว');
        void qc.invalidateQueries({ queryKey: ['pc-closes', companyId] });
      } else {
        setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
      }
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="pc-close-approval-panel"
        style={{
          background: 'white',
          borderRadius: 12,
          width: 560,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              สายอนุมัติการปิด float — {close.voucherNo}
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              คืนเงิน {fmt(Number(close.returnedAmount))} บาท → บัญชี {close.toAccountCode}
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div
          style={{
            padding: 22,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            overflow: 'auto',
          }}
        >
          {approvalStatus === 'REJECTED' && close.rejectedReason && (
            <div
              style={{
                background: 'var(--negative-bg, #fdecea)',
                border: '1px solid var(--negative)',
                borderRadius: 6,
                padding: '10px 14px',
                fontSize: 13,
              }}
              data-testid="pc-close-rejected-reason"
            >
              <strong style={{ color: 'var(--negative)' }}>เหตุผลที่ไม่อนุมัติ:</strong>{' '}
              {close.rejectedReason}
            </div>
          )}
          {approvalStatus === 'RETURNED' && close.returnedReason && (
            <div
              style={{
                background: 'var(--warning-bg, #fef3e8)',
                border: '1px solid var(--warning)',
                borderRadius: 6,
                padding: '10px 14px',
                fontSize: 13,
              }}
              data-testid="pc-close-returned-reason"
            >
              <strong style={{ color: 'var(--warning)' }}>เหตุผลที่ส่งกลับ:</strong>{' '}
              {close.returnedReason}
            </div>
          )}
          {close.custodyNotes && <div className="meta">บันทึกการส่งมอบ: {close.custodyNotes}</div>}
          {isPending && (
            <div className="meta">
              <Icon name="check" size={12} /> รออนุมัติ · ขั้น {currentStep}/{totalSteps}
            </div>
          )}

          <ApprovalTimeline
            approvals={approvals}
            isActive={isPending}
            currentStep={currentStep}
            emptyText="ยังไม่ส่งอนุมัติ"
            leadLine={
              close.submittedBy ? (
                <div className="meta">
                  ส่งอนุมัติโดย {close.submittedBy}
                  {close.submittedAt ? ` · ${fmtDate(close.submittedAt)}` : ''}
                </div>
              ) : undefined
            }
            trailLine={
              close.status === 'POSTED' && close.approvedBy ? (
                <div className="meta">
                  อนุมัติโดย {close.approvedBy}
                  {close.approvedAt ? ` · ${fmtDate(close.approvedAt)}` : ''}
                </div>
              ) : undefined
            }
          />

          {actionError && (
            <div
              style={{ color: 'var(--negative)', fontSize: 14 }}
              data-testid="pc-close-action-error"
            >
              {actionError}
            </div>
          )}
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          {isResubmittable && (
            <Can permission="pettycash.close.create">
              <button
                className="btn primary"
                disabled={submitBusy}
                onClick={() => {
                  void handleSubmit();
                }}
              >
                {submitBusy ? 'กำลังส่ง…' : 'ส่งอนุมัติ'}
              </button>
            </Can>
          )}
          {isPending && (
            <Can permission="pettycash.close.approve">
              <button
                className="btn primary"
                disabled={decideBusy}
                onClick={() => setShowDecide(true)}
              >
                พิจารณา
              </button>
            </Can>
          )}
        </div>
      </div>

      {showDecide && (
        <DecisionModal
          voucherNo={close.voucherNo}
          step={currentStep || 1}
          busy={decideBusy}
          error={actionError}
          titlePrefix="พิจารณาการปิด float"
          onConfirm={(d) => {
            void handleDecide(d);
          }}
          onClose={() => {
            setShowDecide(false);
            setActionError(null);
          }}
        />
      )}
    </div>
  );
}
